import { useCallback, useEffect, useMemo, useState } from 'react';
import { getEnvironmentApiUrl } from '../services/General';
import useHttp from './useHttp';
import moment from 'moment';

import { Lab, ProfileLab } from '../interfaces/Lab';
import { DataElement } from '../interfaces/PhysiologicalMonitoring';
import { Parameter, ProfileSheetSection, ProfileSheetSectionParameters } from '../interfaces/Profile';
import {
    PatientPreconfigDetail,
    PreconfigInquiry,
    PreconfigSheetParameter
} from '../interfaces/Preconfigurations';

const useTablesPatient = (
    caseID: number,
    patientPreconfigID: number | undefined,
    currentProfileID: number | null,
    filteredCVParameters: Parameter[] | null
) => {
    const [profileSections, setProfileSections] = useState<ProfileSheetSectionParameters[]>([]);
    const [dataElementsLab, setDataElementsLab] = useState<DataElement[]>([]);
    const [mergedSheetParamsState, setMergedSheetParamsState] = useState<PreconfigSheetParameter[]>([]);
    const [mergedInquiriesState, setMergedInquiriesState] = useState<PreconfigInquiry[]>([]);

    const mergedSheetParams = useMemo(
        () => (patientPreconfigID == null ? [] : mergedSheetParamsState),
        [patientPreconfigID, mergedSheetParamsState]
    );

    const mergedInquiries = useMemo(
        () => (patientPreconfigID == null ? [] : mergedInquiriesState),
        [patientPreconfigID, mergedInquiriesState]
    );

    const { sendRequest: getPatientPreconfig } = useHttp<PatientPreconfigDetail>();
    const { isLoading: loadingProfileSheet, error: errorProfileSheet, sendRequest: getProfileSheet } = useHttp<ProfileSheetSection[]>();
    const { sendRequest: getLab } = useHttp<Lab[]>();
    const { sendRequest: getProfileLab } = useHttp<ProfileLab[]>();

    const parseLab = (labData: Lab[], profilesLab: ProfileLab[]) => {
        const filteredLab = labData.filter(labTime =>
            profilesLab.some(profile => profile.lab_code_id === labTime.lab_code_id)
        );

        const elements: DataElement[] = [];
        const uniqueLabParams: string[] = [...new Set(filteredLab.map(lab => lab.lab_code_id))];

        setProfileSections(old => old.map(sheet => {
            if (sheet.section.section_code === 'laboratorio') {
                return {
                    ...sheet,
                    parameters: uniqueLabParams.map(labParam => ({
                        id: labParam,
                        parameter: { id: labParam, name: labParam },
                        visible: true,
                    }))
                };
            }
            return sheet;
        }));

        filteredLab.forEach(lab => {
            const date = moment(lab.date).locale('es').format('DD-MM-YYYY HH:mm:ss');
            const existing = elements.find(e => e.date === date);

            if (existing) {
                existing.values[lab.lab_code_id] = Number(lab.value);
            } else {
                elements.push({
                    date,
                    values: { [lab.lab_code_id]: Number(lab.value) },
                    comments: {},
                    colors: {}
                });
            }
        });

        setDataElementsLab(elements);
    };

    const parseProfileLab = useCallback((profilesLab: ProfileLab[]) => {
        getLab({
            url: getEnvironmentApiUrl() + 'lab/all',
            method: 'POST',
            body: { params: {}, filter: { case_id: caseID } },
        }, (data) => parseLab(data, profilesLab));
    }, [getLab, caseID]);

    const mergeSheetParameters = useCallback((base: PreconfigSheetParameter[], override: PreconfigSheetParameter[]) => {
        const map = new Map<string, PreconfigSheetParameter>();

        base.forEach(p => p.sheet_parameter_id && map.set(String(p.sheet_parameter_id), p));

        override.forEach(p => {
            if (!p.sheet_parameter_id) return;
            const key = String(p.sheet_parameter_id);
            if (p.override_delete) map.delete(key);
            else map.set(key, p);
        });

        const result = Array.from(map.values());

        return result;
    }, []);

    const mergePreconfigInquiries = useCallback((base: PreconfigInquiry[], override: PreconfigInquiry[]) => {
        const map = new Map<number, PreconfigInquiry>();

        base.forEach(i => map.set(i.inquiry_id, i));

        override.forEach(i => {
            if (i.override_delete) map.delete(i.inquiry_id);
            else map.set(i.inquiry_id, i);
        });

        const result = Array.from(map.values());

        return result;
    }, []);

    const parseProfileSections = useCallback((
        data: ProfileSheetSection[],
        profileID: number,
        sheetParams: PreconfigSheetParameter[]
    ) => {
        const hasPreconfig = patientPreconfigID != null;

        const preconfigMap = new Map(
            sheetParams
                .filter(p => p.sheet_parameter_id)
                .map(p => [String(p.sheet_parameter_id), p])
        );

        const parsed: ProfileSheetSectionParameters[] = data.map(section => {
            if (section.section.section_code === 'cv') {
                const baseParamsFromSection = section.parameters.map(p => ({
                    ...p.parameter,
                    id: String(p.parameter.id)
                }));

                const baseParamsMap = new Map(
                    baseParamsFromSection.map(param => [String(param.id), param])
                );

                const outletParams =
                    filteredCVParameters && filteredCVParameters.length > 0
                        ? filteredCVParameters.map(param => ({
                            ...param,
                            id: String(param.id)
                        }))
                        : [];

                const outletParamsMap = new Map(
                    outletParams.map(param => [String(param.id), param])
                );

                const finalParamsMap = new Map<string, Parameter>();

                if (hasPreconfig) {
                    sheetParams.forEach(sheetParam => {
                        const key = String(sheetParam.sheet_parameter_id);
                        if (!key) return;

                        if (outletParamsMap.has(key)) {
                            finalParamsMap.set(key, outletParamsMap.get(key)!);
                            return;
                        }

                        if (baseParamsMap.has(key)) {
                            finalParamsMap.set(key, baseParamsMap.get(key)!);
                            return;
                        }

                        finalParamsMap.set(key, {
                            id: key,
                            name: key,
                            visible: true,
                            min_limit: sheetParam.min_limit ?? undefined,
                            max_limit: sheetParam.max_limit ?? undefined,
                        } as Parameter);
                    });

                    outletParams.forEach(param => {
                        const key = String(param.id);
                        if (!finalParamsMap.has(key)) {
                            finalParamsMap.set(key, param);
                        }
                    });
                } else {
                    outletParams.forEach(param => {
                        finalParamsMap.set(String(param.id), param);
                    });
                }

                const finalParams = Array.from(finalParamsMap.values()).map(param => {
                    const id = String(param.id);
                    const override = hasPreconfig ? preconfigMap.get(id) : undefined;

                    return {
                        ...param,
                        id,
                        min_limit: override?.min_limit ?? param.min_limit,
                        max_limit: override?.max_limit ?? param.max_limit
                    };
                });

                return {
                    ...section,
                    parameters: finalParams
                };
            }

            return {
                ...section,
                parameters: section.parameters.map(p => ({
                    ...p.parameter,
                    id: String(p.parameter.id)
                }))
            };
        });

        setProfileSections(parsed);

        const lab = parsed.find(s => s.section.section_code === 'laboratorio');

        if (lab) {
            getProfileLab({
                url: getEnvironmentApiUrl() + 'profile_lab/all',
                method: 'POST',
                body: { params: {}, filter: { profile_id: profileID } },
            }, parseProfileLab);
        } else {
            setDataElementsLab([]);
        }
    }, [
        patientPreconfigID,
        filteredCVParameters,
        getProfileLab,
        parseProfileLab
    ]);

    useEffect(() => {
        if (patientPreconfigID == null) {
            return;
        }

        getPatientPreconfig({
            url: getEnvironmentApiUrl() + 'preconfig/patient_preconfig/' + patientPreconfigID,
            method: 'GET',
        }, (data) => {
            const nextMergedSheetParams = mergeSheetParameters(
                data.preconfig?.preconfig_sheet_parameters ?? [],
                data.override_preconfig?.preconfig_sheet_parameters ?? []
            );

            const nextMergedInquiries = mergePreconfigInquiries(
                data.preconfig?.preconfig_inquiries ?? [],
                data.override_preconfig?.preconfig_inquiries ?? []
            );

            setMergedSheetParamsState(nextMergedSheetParams);
            setMergedInquiriesState(nextMergedInquiries);
        });
    }, [patientPreconfigID, getPatientPreconfig, mergeSheetParameters, mergePreconfigInquiries]);

    useEffect(() => {
        if (currentProfileID === null) {
            return;
        }

        getProfileSheet({
            url: getEnvironmentApiUrl() + 'profile_sheet_section/all',
            method: 'POST',
            body: { params: {}, filter: { visible: true, profile_id: currentProfileID } },
        }, (data) => {
            parseProfileSections(data, currentProfileID, mergedSheetParams);
        });
    }, [currentProfileID, mergedSheetParams, getProfileSheet, parseProfileSections, filteredCVParameters]);

    return {
        loading: loadingProfileSheet,
        error: errorProfileSheet,
        profileSections,
        dataElementsLab,
        mergedInquiries,
        mergedSheetParams
    };
};

export default useTablesPatient;